import { FCM_SW_PATH, FCM_SW_SCOPE } from './fcm-sw.js';
import {
  detectPlatform,
  readPushEnvironment,
  tokenDocId,
  type PushPlatform,
} from './push-state.js';

/**
 * The push plumbing every app shares: register the worker, fetch the FCM token,
 * keep `users/{uid}/fcmTokens/{tokenId}` fresh, and remove it on sign-out.
 *
 * Firebase is INJECTED (the app wraps `getToken`/`deleteToken`/`onMessage`
 * from its own `firebase/messaging` instance) so this package carries no
 * Firebase dependency and the logic is testable with fakes.
 */

export interface PushForegroundMessage {
  readonly title: string | null;
  readonly body: string;
  readonly deepLink: string | null;
  /** `data.tenantId` when the payload carries one — lets a white-label host drop another operator's message. */
  readonly tenantId: string | null;
  readonly receivedAt: number;
}

export interface PushTokenStore {
  /** Create or refresh the device's token doc (keep `createdAt`, bump `lastUsedAt`). */
  upsert(uid: string, docId: string, token: string, platform: PushPlatform): Promise<void>;
  remove(uid: string, docId: string): Promise<void>;
}

export interface PushMessagingAdapter {
  isSupported(): Promise<boolean>;
  getToken(registration: ServiceWorkerRegistration): Promise<string>;
  deleteToken(): Promise<boolean>;
  /** Subscribe to foreground messages; returns an unsubscribe. */
  onForegroundMessage(cb: (msg: PushForegroundMessage) => void): Promise<() => void>;
}

export interface PushLogger {
  warn(msg: string, ctx?: Record<string, unknown>): void;
  error(msg: string, ctx?: Record<string, unknown>): void;
}

export interface PushClientConfig {
  readonly messaging: PushMessagingAdapter;
  readonly tokens: PushTokenStore;
  readonly logger: PushLogger;
  /** False when the app was built without VITE_FCM_VAPID_KEY: tokens can't be minted. */
  readonly vapidKeyPresent: boolean;
  /**
   * The router base this page lives under (`/sparkle/`, or `/`). Passed to the
   * worker so a tap on a root-relative link still lands in this tenant.
   */
  readonly appBase: string;
}

export interface PushClient {
  readonly config: PushClientConfig;
  isSupported(): Promise<boolean>;
  /** Ask the browser, then register the token for `uid`. Returns the resulting permission. */
  enable(uid: string): Promise<NotificationPermission | 'unsupported'>;
  /** Permission already granted: mint/refresh the token and bump `lastUsedAt`. */
  refresh(uid: string): Promise<boolean>;
  /** Before auth switches users: delete the token doc and the FCM token. */
  cleanupForSignOut(uid: string): Promise<void>;
}

export function createPushClient(config: PushClientConfig): PushClient {
  let lastToken: string | null = null;

  async function registration(): Promise<ServiceWorkerRegistration | null> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
    const url = `${FCM_SW_PATH}?base=${encodeURIComponent(config.appBase)}`;
    return navigator.serviceWorker.register(url, { scope: FCM_SW_SCOPE });
  }

  async function mintToken(): Promise<string | null> {
    if (!config.vapidKeyPresent) {
      config.logger.warn('push: VAPID key missing — cannot mint an FCM token');
      return null;
    }
    const reg = await registration();
    if (!reg) return null;
    const token = await config.messaging.getToken(reg);
    if (!token) return null;
    lastToken = token;
    return token;
  }

  async function store(uid: string): Promise<boolean> {
    const token = await mintToken();
    if (!token) return false;
    const platform = detectPlatform(typeof navigator !== 'undefined' ? navigator.userAgent : '');
    await config.tokens.upsert(uid, tokenDocId(token), token, platform);
    return true;
  }

  return {
    config,
    async isSupported() {
      try {
        return await config.messaging.isSupported();
      } catch {
        return false;
      }
    },
    async enable(uid) {
      if (typeof Notification === 'undefined') return 'unsupported';
      const result = await Notification.requestPermission();
      if (result === 'granted') {
        let stored: boolean;
        try {
          stored = await store(uid);
        } catch (e) {
          config.logger.warn('push: token registration failed after grant', { error: String(e) });
          throw e;
        }
        // Granted but no token (no VAPID key in this build, no worker): the
        // browser says yes and nothing will ever arrive. Throwing is what makes
        // the provider show "couldn't connect this device" instead of "On".
        if (!stored) throw new Error('push: permission granted but this device was not registered');
      }
      return result;
    },
    async refresh(uid) {
      if (readPushEnvironment(true).permission !== 'granted') return false;
      try {
        return await store(uid);
      } catch (e) {
        config.logger.warn('push: token refresh failed', { error: String(e) });
        return false;
      }
    },
    async cleanupForSignOut(uid) {
      if (readPushEnvironment(true).permission !== 'granted') return;
      try {
        const token = lastToken ?? (await mintToken());
        if (!token) return;
        // Order matters: the doc delete needs the OUTGOING user's auth.
        await config.tokens.remove(uid, tokenDocId(token));
        await config.messaging.deleteToken();
        lastToken = null;
      } catch (e) {
        // Never block sign-out on push hygiene; the server prunes dead tokens.
        config.logger.warn('push: sign-out cleanup failed', { error: String(e) });
      }
    },
  };
}
